/**
 * =========================================================================================
 * Account Main component
 * =========================================================================================
 *
 */

import React, {lazy, Suspense, useEffect, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Redirect, Route, Switch, useRouteMatch} from 'react-router-dom';
import * as Icon from "ubs-icons";
import UPermissionPopup from "ubs-framework/v2/UPermissionPopup";
import UVersionPopup from "ubs-framework/v2/UVersionPopup";
import {Error404} from "../common/error/Error";
import {sendData} from "../../store/actions";
import SideMenuLayout from "../../components/side-menu-layout";
import managePlan from "./plans/components/manage-plan";
import settings from "./settings";
import PricingPlan from './billing/subscription/pricing-plan';
import BillingPayments from './billing/payments';
import PaymentMethod from './billing/payment-method';
import BillingActivity from './billing/activity';
import CashfreeCallback from './billing/subscription/cashfree-callback';
import SubscriptionCheckout from './billing/subscription/checkout';
import OrganizationOverview from './organization-overview';
import workspace from '../sign-up-v2/workspace';
import ResumePaymentBanner from '../common/components/resume-payment-banner';
import UModal from 'ubs-framework/v2/UModal/NewUModal';
import UPreparingData from 'ubs-framework/v2/UPreparingData';
import useSocket from '../common/useSocket';
import { COLOR, PLAN } from 'ubs-framework/v2/common/constant';
import UErrorPage from 'ubs-framework/v2/UErrorPages/UErrorPage';
import PrivateRoute from '../../routes/PrivateRoute';
import usePlan from '../../common/hooks/usePlan';
// Lazy Loading Routing

const WorkSpace_view = lazy(()=> import("./workspace_view"))
const Plans = lazy(()=> import("./plans"))
const Settings = lazy(()=> import("./settings"))
const ActiveSessions = lazy(()=> import("./active-sessions"))
const ActiveSessionsReport = lazy(()=> import("./active-sessions-report"))
const ApiUsageMonitor = lazy(()=> import("./api-usage-monitor"))
const BillingWallet = lazy(()=> import("./billing/wallet"))
const BillingInvoiceDashboard = lazy(()=> import("./billing/dashboard"))
const BillingHistory = lazy(()=> import("./billing/history"))
const BillingInvoices = lazy(()=> import("./billing/invoices"))
const BillingReceipts = lazy(()=> import("./billing/receipts"))
const BillingReports = lazy(()=> import("./billing/reports"))
const BillingTdsClaim = lazy(()=> import("./billing/tds-claim"))
const Organization = React.memo(() => {
    const {path, url} = useRouteMatch();
    const __plan = usePlan();
    const _WI_HRMS = __plan(PLAN?.whatsappIntegration, 'and', 'hrms')
    const _WI_REC = __plan(PLAN?.whatsappIntegration, 'and', 'recruit')
    const _WI_PMS = __plan(PLAN?.whatsappIntegration, 'and', 'pms')
    const _WI_PAY = __plan(PLAN?.whatsappIntegration, 'and', 'payroll')
    const _AP_LINK = __plan(PLAN?.aadhaarPanLinkStatus, 'and', 'hrms')
    const _AP_VERIFY = __plan(PLAN?.aadhaarCardVerification, 'and', 'hrms')
    const _PAN_V = __plan(PLAN?.panCardVerification, 'and', 'hrms')

    const _PI = useSelector(e => e?.common?.planInfo);
    const _OWI = useSelector(e => e?.common?.ownerId);
    const _ID = useSelector(e => e?.common?._id);
    const _UT = useSelector(e => {
        const userType = e?.common?.orgList?.find(_ => _?.orgId === e?.common?.orgId)?.userType
        return (userType?.hrms === 3 || userType?.recruit === 1 || userType?.pms === 3)
    });
    // Inactive Employee Login: a restricted session may only use Workspace in the Account module. Any
    // other manage route (active-sessions, api-usage-monitor, billing, etc.) is redirected back to
    // Workspace even when hit directly by URL. Per-org flag carried on currentOrg from getWorkspaceList.
    const _IIR = useSelector(e => e?.common?.currentOrg?.isInactiveRestricted === true);
    // Wallet Billing (Phase 8) — null (not yet resolved) is treated as pass so legit users never see a
    // false-404 flash while billing|wallet|client|gate is in flight; only an explicit false blocks.
    const _WALLET_GATE = useSelector(e => e?.common?.walletBillingGate);
    const tabBlocked = (tab) => _WALLET_GATE?.hasAccess === false || _WALLET_GATE?.tabs?.[tab] === false;
    const BillingGateLoader = () => <div className="u_preloader"><Icon.Preloader/></div>;
    const billingRoute = (tab, Cmp) => (!_WALLET_GATE ? BillingGateLoader : tabBlocked(tab) ? Error404 : Cmp);

    return (
        <React.Fragment>
            {/** Lazy Loading sub router setup using code splitting functionality */}
            {/*<div className="u_ats u_relative u_ats-app-view">*/}
                <Suspense fallback={<div className="u_preloader"><Icon.Preloader/></div>}>
                    <Switch>
                        <Route exact path={`${path}`}><Redirect to={`${url}/workspace`}/></Route>

                        {/** Workspace **/}
                        <Route path={`${path}/workspace`} component={WorkSpace_view}/> {/** Workspace_view */}

                        {/** Inactive Employee Login: restricted sessions can only reach Workspace. Placed
                             right after the Workspace route so Switch serves Workspace normally and any
                             other manage URL (typed directly) redirects here back to Workspace. **/}
                        {_IIR && <Route path={`${path}`}><Redirect to={`${url}/workspace`}/></Route>}

                        {/** Plans **/}
                        {/* <Route exact path={`${path}/plans`} component={true ? Plans : Error404} />
                        <Route exact path={`${path}/plans/manageplan`} component={true ? managePlan : Error404} /> */}

                        {/** Organization Overview **/}
                        <Route path={`${path}/organization-overview`} component={OrganizationOverview}/>

                        {/** Active Sessions **/}
                        <Route path={`${path}/active-sessions`} component={ActiveSessions}/>

                        {/** Active Sessions Report **/}
                        <Route path={`${path}/active-sessions-report`} component={(_OWI === _ID || _UT) ? ActiveSessionsReport : Error404}/>

                        {/** API Usage Monitor (Aadhaar & PAN) — open like Active Sessions/Workspace (Account UAC has no hrms.report node) **/}
                        <Route path={`${path}/api-usage-monitor`} component={ ((_OWI === _ID || _UT) && (_WI_REC || _WI_HRMS || _AP_LINK || _AP_VERIFY || _PAN_V || _WI_PMS || _WI_PAY)) ? ApiUsageMonitor : Error404}/>

                        {/** Billing **/}
                        <Route path={`${path}/pricing-plan`} component={(_PI?.isTrialAccount && _OWI === _ID) ? PricingPlan : Error404}/>
                        <Route path={`${path}/subscriptions/payments`} component={(_PI?.isTrialAccount && _OWI === _ID) ? BillingPayments : Error404}/>
                        <Route path={`${path}/subscriptions/payment-method`} component={(_PI?.isTrialAccount && _OWI === _ID) ? PaymentMethod : Error404}/>
                        <Route path={`${path}/subscriptions/activity`} component={(_PI?.isTrialAccount && _OWI === _ID) ? BillingActivity : Error404}/>
                        <Route path={`${path}/subscriptions/checkout`} component={(_PI?.isTrialAccount && _OWI === _ID) ? SubscriptionCheckout : Error404}/>
                        <Route path={`${path}/cashfree`} component={(_PI?.isTrialAccount && _OWI === _ID) ? CashfreeCallback : Error404}/>

                        <Route path={`${path}/billing/dashboard`} component={billingRoute('dashboard', BillingInvoiceDashboard)}/>
                        <Route path={`${path}/billing/wallet`} component={billingRoute('wallet', BillingWallet)}/>
                        <Route path={`${path}/billing/history`} component={billingRoute('history', BillingHistory)}/>
                        <Route path={`${path}/billing/invoices`} component={billingRoute('invoices', BillingInvoices)}/>
                        <Route path={`${path}/billing/receipts`} component={billingRoute('receipts', BillingReceipts)}/>
                        <Route path={`${path}/billing/reports`} component={billingRoute('reports', BillingReports)}/>
                        <Route path={`${path}/billing/tds-claim`} component={billingRoute('tdsClaim', BillingTdsClaim)}/>

                        {/** Old wallet-billing route (Phase 8) — redirect to the new Wallet dashboard **/}
                        <Route path={`${path}/wallet-billing`}><Redirect to={`${url}/billing/wallet`}/></Route>

                        {/** Settings **/}
                        {/* <Route path={`${path}/settings`} component={true ? settings : Error404}/> */}

                        <Route exact path="*" component={Error404}/>
                    </Switch>
                </Suspense>
            {/*</div>*/}
        </React.Fragment>
    );
});

const ManageAccount = () => {
    const dispatch = useDispatch();
    const _R = useSelector(e => e.common.release);
    const _VERSION = useSelector(e => e.common.version);
    const _UAC_ID = true || useSelector(e => e.performance.uacID);
    const _FILES = useSelector(e => e.common.files);
    const _UAC_UPDATE = useSelector(e => e.common?.uacUpdate);
    const _ID = useSelector(e => e?.common?._id);
    const _PI = useSelector(e => e?.common?.planInfo);
    const _OWI = useSelector(e => e?.common?.ownerId);
    const _IS_PREPARING_DATA = useSelector(e => e.common?.isPreparingData);
    const _IS_SAMPLE_DATA = useSelector(e => e.common?.isSampleData);
    const _IS_SETUP_DONE = useSelector(e => e.common?.isSetupDone);
    const _SOCKET_ID = useSelector(e => e.common?.socketId);
    const _RESUME_PAYMENT = useSelector(e => e.common?.pendingPayment);
    const [version, setVersion] = useState(0);

    useEffect(() => {
        // sendData(dispatch, 'getWorkspaceList', {});
        sendData(dispatch, 'readProductVideo', {});
        sendData(dispatch, 'getLatestVersion', {});
        // if(_ID) sendData(dispatch, 'hrms|employee|userInfo|read', {user:_ID});
    }, []);

    useEffect(() => {
        if (version === 0) setVersion(_VERSION);
    }, [_VERSION]);

     const handleRefresh = () => {
        dispatch({type: 'SET_PREPARING_DATA', payload: false});
        dispatch({type: 'SET_SETUP_DONE', payload: {flag: true}});
        window.location.reload(true);
    }

    return (
        <React.Fragment>
            {_PI?.isTrialAccount && (_OWI === _ID) && _RESUME_PAYMENT && <ResumePaymentBanner />}
            <SideMenuLayout>
                {/** Router setup using nesting route functionality */}
                <Suspense fallback={<div>Loading...</div>}>
                    <Switch>
                        <PrivateRoute path="/:orgId/manage" component={Organization}/>
                    </Switch>
                </Suspense>
                {version !== 0 && _VERSION !== version &&
                    <UVersionPopup
                        close={() => setVersion(_VERSION)}
                        moduleIcon={<Icon.Performance size={32}/>}
                        moduleName="Super Performance"
                        refresh={() => window.location.reload()}/>
                }
                {(_UAC_UPDATE?._id === _UAC_ID || _UAC_UPDATE?.force) &&
                    <UPermissionPopup
                        moduleIcon={<Icon.Performance size={32}/>}
                        moduleName="Super Performance"
                        refresh={() => window.location.reload()}/>}
                    
            </SideMenuLayout>

            {_IS_PREPARING_DATA && typeof _IS_SAMPLE_DATA === 'boolean' &&
                <UModal
                    open={true}
                    isHideCloseIcon
                    isHeaderShow={false}
                    maxWidth={480}
                    zIndex={999}
                >
                    <div className="u_p-24 u_pt-32">
                        <UPreparingData
                            useSocket={useSocket}
                            socketId={_SOCKET_ID}
                            eventName1={"track|master|sampledata|createpercentage"}
                            eventName2={"track|master|sampledata|removepercentage"}
                            isLoading={_IS_SETUP_DONE?.isLoading}
                            title={`We’re removing sample data.`} description="This won’t take long—just a minute or two."
                            successTitle={`Sample data removed!`} successDescription={`Your account is now ready with removed sample data. <br/> To view it, click Refresh.`}
                            buttonTitle="Refresh"
                            buttonStyle={{ width: 'auto', minWidth: '120px', borderRadius: '6px' }}
                            buttonIcon={<Icon.Reload size={14} fill={COLOR.u_white} className="u_mr-8" />}
                            onSubmit={() => handleRefresh()}
                        />
                    </div>
                </UModal>
            }

        </React.Fragment>
    );
};

export default ManageAccount;
